export type RelevanceLevel = 'alta' | 'media' | 'baixa';

export interface RelevanceMetricDetail {
    entropiaNormalizada: number;
    quiQuadrado: number;
    coeficienteVariacao: number;
}

export interface ParameterRelevance {
    id: string;
    categoria: 'numeros' | 'sorteios';
    parametro: string;
    peso: number;
    relevante: boolean;
    nivel: RelevanceLevel;
    metricas: RelevanceMetricDetail;
    interpretacao: string;
}

const RELEVANCE_THRESHOLD = 40;

const shannonEntropy = (counts: number[]) => {
    const total = counts.reduce((acc, value) => acc + value, 0);
    if (total <= 0) {
        return 0;
    }

    return counts.reduce((entropy, count) => {
        if (count <= 0) {
            return entropy;
        }
        const probability = count / total;
        return entropy - probability * Math.log2(probability);
    }, 0);
};

const normalizedEntropy = (counts: number[]) => {
    const activeCategories = counts.filter((value) => value > 0).length;
    if (activeCategories <= 1) {
        return 0;
    }

    return shannonEntropy(counts) / Math.log2(activeCategories);
};

const chiSquareUniform = (counts: number[]) => {
    const categories = counts.length;
    const total = counts.reduce((acc, value) => acc + value, 0);

    if (total <= 0 || categories === 0) {
        return 0;
    }

    const expected = total / categories;
    if (expected <= 0) {
        return 0;
    }

    return counts.reduce((sum, count) => sum + Math.pow(count - expected, 2) / expected, 0);
};

const coefficientOfVariation = (values: number[]) => {
    if (values.length === 0) {
        return 0;
    }

    const mean = values.reduce((acc, value) => acc + value, 0) / values.length;
    if (mean === 0) {
        return 0;
    }

    const variance = values.reduce((acc, value) => acc + Math.pow(value - mean, 2), 0) / values.length;
    return Math.sqrt(variance) / mean;
};

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const getRelevanceLevel = (weight: number): RelevanceLevel => {
    if (weight >= 70) {
        return 'alta';
    }
    if (weight >= RELEVANCE_THRESHOLD) {
        return 'media';
    }
    return 'baixa';
};

const buildInterpretation = (
    parametro: string,
    weight: number,
    metrics: RelevanceMetricDetail
) => {
    const nivel = getRelevanceLevel(weight);

    if (nivel === 'alta') {
        return `${parametro} apresenta padrão estatístico forte (entropia ${(metrics.entropiaNormalizada * 100).toFixed(0)}%, χ² ${metrics.quiQuadrado.toFixed(1)}). Vale usar como critério de filtro ou peso na estratégia.`;
    }

    if (nivel === 'media') {
        return `${parametro} mostra variação moderada nos sorteios analisados. Pode complementar outros filtros, mas sozinho tem poder preditivo limitado.`;
    }

    return `${parametro} está próximo de uma distribuição uniforme no histórico. Filtrar por este critério tende a ter baixo impacto na seleção de combinações.`;
};

const computeDistributionRelevance = (
    id: string,
    categoria: 'numeros' | 'sorteios',
    parametro: string,
    counts: number[]
): ParameterRelevance | null => {
    if (!Array.isArray(counts) || counts.length === 0) {
        return null;
    }

    const sanitized = counts.map((value) => Math.max(0, Number(value) || 0));
    const total = sanitized.reduce((acc, value) => acc + value, 0);

    if (total <= 0) {
        return null;
    }

    const entropyNorm = normalizedEntropy(sanitized);
    const chi2 = chiSquareUniform(sanitized);
    const cv = coefficientOfVariation(sanitized);

    const chiNorm = clamp01(chi2 / (Math.max(sanitized.length, 1) * 3));
    const cvNorm = clamp01(cv / 1.5);
    const concentrationScore = 1 - entropyNorm;

    const peso = Math.round(
        concentrationScore * 0.4 * 100
        + chiNorm * 0.4 * 100
        + cvNorm * 0.2 * 100
    );

    const metricas = {
        entropiaNormalizada: Number(entropyNorm.toFixed(3)),
        quiQuadrado: Number(chi2.toFixed(2)),
        coeficienteVariacao: Number(cv.toFixed(3)),
    };

    return {
        id,
        categoria,
        parametro,
        peso,
        relevante: peso >= RELEVANCE_THRESHOLD,
        nivel: getRelevanceLevel(peso),
        metricas,
        interpretacao: buildInterpretation(parametro, peso, metricas),
    };
};

const extractQtdValues = (items: Array<{ qtd?: number }> = []) =>
    items.map((item) => Number(item.qtd) || 0);

const computeAtrasoTimelineRelevance = (atrasoGraf: number[][] = []) => {
    if (!Array.isArray(atrasoGraf) || atrasoGraf.length === 0) {
        return null;
    }

    const stdByDraw = atrasoGraf.map((draw) => {
        const values = draw.map((value) => Number(value) || 0);
        if (values.length === 0) {
            return 0;
        }
        const mean = values.reduce((acc, value) => acc + value, 0) / values.length;
        const variance = values.reduce((acc, value) => acc + Math.pow(value - mean, 2), 0) / values.length;
        return Math.sqrt(variance);
    });

    return computeDistributionRelevance(
        'numeros-atraso-evolucao',
        'numeros',
        'Evolução do atraso ao longo dos sorteios',
        stdByDraw
    );
};

export const computeParameterRelevance = (resultAnalise: any): ParameterRelevance[] => {
    if (!resultAnalise) {
        return [];
    }

    const combinacoes = resultAnalise.combinacoes ?? [];
    const numeros = resultAnalise.numeros ?? [];

    const candidates: Array<ParameterRelevance | null> = [
        computeDistributionRelevance(
            'numeros-atraso-atual',
            'numeros',
            'Atraso atual por número',
            (numeros[0]?.atraso ?? []).map((item: { atraso?: number }) => Number(item.atraso) || 0)
        ),
        computeDistributionRelevance(
            'numeros-frequencia',
            'numeros',
            'Frequência histórica (vezes que saiu)',
            (numeros[3]?.vezes_saiu ?? []).map((item: { vezes_saiu?: number }) => Number(item.vezes_saiu) || 0)
        ),
        computeDistributionRelevance(
            'numeros-atraso-ao-sair-geral',
            'numeros',
            'Jogos sem sair quando o número saiu (geral)',
            extractQtdValues(numeros[4]?.atraso_ao_sair_geral)
        ),
        computeAtrasoTimelineRelevance(numeros[1]?.atraso_graf),
        computeDistributionRelevance(
            'sorteios-media-atraso',
            'sorteios',
            'Média de jogos sem sair entre os números sorteados',
            extractQtdValues(combinacoes[24]?.media_atraso_sorteio)
        ),
        computeDistributionRelevance(
            'sorteios-sequencias',
            'sorteios',
            'Sequências por jogo',
            extractQtdValues(combinacoes[0]?.sequencias)
        ),
        computeDistributionRelevance(
            'sorteios-sequencia-direta-max',
            'sorteios',
            'Sequência direta máxima',
            extractQtdValues(combinacoes[1]?.sequencias_dire_max)
        ),
        computeDistributionRelevance(
            'sorteios-pares',
            'sorteios',
            'Quantidade de números pares',
            extractQtdValues(combinacoes[2]?.num_pares)
        ),
        computeDistributionRelevance(
            'sorteios-fibonacci',
            'sorteios',
            'Números de Fibonacci',
            extractQtdValues(combinacoes[3]?.qtd_fibonacci)
        ),
        computeDistributionRelevance(
            'sorteios-primos',
            'sorteios',
            'Números primos',
            extractQtdValues(combinacoes[4]?.qtd_primos)
        ),
        computeDistributionRelevance(
            'sorteios-linhas',
            'sorteios',
            'Quantidade de linhas',
            extractQtdValues(combinacoes[5]?.qtd_linhas)
        ),
        computeDistributionRelevance(
            'sorteios-max-linha',
            'sorteios',
            'Quantidade máxima por linha',
            extractQtdValues(combinacoes[6]?.qtd_max_linhas)
        ),
        computeDistributionRelevance(
            'sorteios-colunas',
            'sorteios',
            'Quantidade de colunas',
            extractQtdValues(combinacoes[7]?.qtd_colunas)
        ),
        computeDistributionRelevance(
            'sorteios-max-coluna',
            'sorteios',
            'Quantidade máxima por coluna',
            extractQtdValues(combinacoes[8]?.qtd_max_colunas)
        ),
        computeDistributionRelevance(
            'sorteios-dist-maior-menor',
            'sorteios',
            'Distância entre o maior e o menor número',
            extractQtdValues(combinacoes[18]?.dis_prim_ultimo)
        ),
        computeDistributionRelevance(
            'sorteios-qtd-ate-metade',
            'sorteios',
            'Quantidade de números até a metade do volante',
            extractQtdValues(combinacoes[19]?.qtd_ate_metade)
        ),
        computeDistributionRelevance(
            'sorteios-media-numeros',
            'sorteios',
            'Média entre os números (arredondada para baixo)',
            extractQtdValues(combinacoes[20]?.media_numeros)
        ),
        computeDistributionRelevance(
            'sorteios-dist-media-consecutiva',
            'sorteios',
            'Distância média entre números consecutivos',
            extractQtdValues(combinacoes[21]?.distancia_media_consecutiva)
        ),
        computeDistributionRelevance(
            'sorteios-dist-abaixo-media',
            'sorteios',
            'Distâncias consecutivas abaixo da média',
            extractQtdValues(combinacoes[22]?.distancias_abaixo_media)
        ),
        computeDistributionRelevance(
            'sorteios-dist-acima-media',
            'sorteios',
            'Distâncias consecutivas acima da média',
            extractQtdValues(combinacoes[23]?.distancias_acima_media)
        ),
        computeDistributionRelevance(
            'sorteios-menor-num',
            'sorteios',
            'Menor número do jogo',
            extractQtdValues(combinacoes[9]?.menor_num)
        ),
        computeDistributionRelevance(
            'sorteios-maior-num',
            'sorteios',
            'Maior número do jogo',
            extractQtdValues(combinacoes[10]?.maior_num)
        ),
        computeDistributionRelevance(
            'sorteios-quadrantes',
            'sorteios',
            'Quadrantes utilizados (4 quadrantes)',
            extractQtdValues(combinacoes[11]?.qtd_quad_em_4_quad)
        ),
        computeDistributionRelevance(
            'sorteios-max-quadrante',
            'sorteios',
            'Quantidade máxima por quadrante',
            extractQtdValues(combinacoes[12]?.qtd_max_quad_em_4_quad)
        ),
        computeDistributionRelevance(
            'sorteios-rep-ultimo',
            'sorteios',
            'Repetição do jogo anterior',
            extractQtdValues(combinacoes[13]?.qtd_rep_ultimo)
        ),
        computeDistributionRelevance(
            'sorteios-rep-2',
            'sorteios',
            'Repetição nos 2 jogos anteriores',
            extractQtdValues(combinacoes[14]?.qtd_rep_2_ultimo)
        ),
        computeDistributionRelevance(
            'sorteios-rep-3',
            'sorteios',
            'Repetição nos 3 jogos anteriores',
            extractQtdValues(combinacoes[15]?.qtd_rep_3_ultimo)
        ),
        computeDistributionRelevance(
            'sorteios-rep-4',
            'sorteios',
            'Repetição nos 4 jogos anteriores',
            extractQtdValues(combinacoes[16]?.qtd_rep_4_ultimo)
        ),
        computeDistributionRelevance(
            'sorteios-rep-5',
            'sorteios',
            'Repetição nos 5 jogos anteriores',
            extractQtdValues(combinacoes[17]?.qtd_rep_5_ultimo)
        ),
    ];

    return candidates
        .filter((item): item is ParameterRelevance => item !== null)
        .sort((a, b) => b.peso - a.peso);
};

export const getRelevanceLevelLabel = (nivel: RelevanceLevel) => {
    switch (nivel) {
        case 'alta':
            return 'Alta';
        case 'media':
            return 'Média';
        default:
            return 'Baixa';
    }
};
